import type {
  WelcomeBonusCampaign,
  WelcomeBonusGameWeightingContributions,
  WelcomeBonusScope,
} from '../../../../@types/api';
import {
  AFFILIATE_BONUS_CATEGORY,
  AFFILIATE_CAMPAIGN_PRODUCT,
  WELCOME_BONUS_WAGERING_MODE,
  WELCOME_BONUS_SCOPE,
} from '../../../../constants/enums';
import type { WelcomeBonusCreateFormData, WelcomeBonusEditFormData } from '../../../../schema';
import { buildCasinoProductPayload } from './products/casino';
import { buildSportsbookProductPayload } from './products/sportsbook';

export type WelcomeBonusPayload = Omit<
  WelcomeBonusCreateFormData,
  | 'bonusCategory'
  | 'wageringMode'
  | 'bonusPercentage'
  | 'fixedAmount'
  | 'wageringRequirement'
  | 'multiplier'
  | 'gameWeightingContributions'
  | 'incrementalUnlocksEnabled'
  | 'unlockBatchPercentage'
  | 'maxBetLimit'
  | 'minSelections'
  | 'minOddsPerSelection'
  | 'welcomeBonusScope'
  | 'affiliateIds'
> & {
  welcomeBonusScope: WelcomeBonusScope;
  affiliateIds?: string[];
  bonusPercentage?: number | null;
  fixedAmount?: number | null;
  wageringRequirement?: number | null;
  multiplier?: number | null;
  minSelections?: number | null;
  minOddsPerSelection?: number | null;
  maxBetLimit?: number | null;
  gameWeightingContributions?: WelcomeBonusGameWeightingContributions;
  incrementalUnlocksEnabled?: boolean;
  unlockBatchPercentage?: number | null;
};

export const buildWelcomeBonusPayload = (data: WelcomeBonusCreateFormData): WelcomeBonusPayload => {
  const {
    bonusCategory,
    wageringMode,
    bonusPercentage,
    fixedAmount,
    wageringRequirement,
    multiplier,
    minSelections,
    minOddsPerSelection,
    maxBetLimit,
    gameWeightingContributions,
    incrementalUnlocksEnabled,
    unlockBatchPercentage,
    welcomeBonusScope,
    affiliateIds,
    ...basePayload
  } = data;

  if (!welcomeBonusScope) throw new Error('Cannot build a welcome bonus payload without a scope.');

  const amountPayload = bonusCategory === AFFILIATE_BONUS_CATEGORY.PERCENTAGE ? { bonusPercentage } : { fixedAmount };
  const capLimit = bonusCategory === AFFILIATE_BONUS_CATEGORY.FIXED_AMOUNT ? (fixedAmount ?? null) : data.capLimit;
  const wageringPayload =
    wageringMode === WELCOME_BONUS_WAGERING_MODE.FIXED_AMOUNT ? { wageringRequirement } : { multiplier };
  // Only the selected Product contributes configuration keys to the flat CRM payload.
  const productPayload =
    data.product === AFFILIATE_CAMPAIGN_PRODUCT.CASINO
      ? buildCasinoProductPayload(data)
      : buildSportsbookProductPayload(data);

  return {
    ...basePayload,
    welcomeBonusScope,
    ...(welcomeBonusScope === WELCOME_BONUS_SCOPE.SPECIAL && { affiliateIds }),
    capLimit,
    ...amountPayload,
    ...wageringPayload,
    ...productPayload,
  };
};

export const getWelcomeBonusEditFormData = (item: WelcomeBonusCampaign): WelcomeBonusEditFormData => ({
  name: item.name,
  status: item.status,
  description: item.description ?? '',
  affiliateIds: item.affiliateIds ?? [],
});

export type WelcomeBonusEditDirtyFields = Partial<Record<keyof WelcomeBonusEditFormData, boolean>>;

export const buildWelcomeBonusEditPayload = (
  data: WelcomeBonusEditFormData,
  dirtyFields: WelcomeBonusEditDirtyFields
) => {
  const payload: Partial<WelcomeBonusEditFormData> = {};

  if (dirtyFields.name) payload.name = data.name;
  if (dirtyFields.status) payload.status = data.status;
  if (dirtyFields.description) payload.description = data.description;
  if (dirtyFields.affiliateIds) payload.affiliateIds = data.affiliateIds;

  return payload;
};
